import { Chess } from "@vendor/chess.js";

export const SCORECARD_SLOTS = 15;
export const MEDAL_DEPTH = 10;
/** Legacy name: the number of lines a medal needs at minimum. */
export const MEDAL_LINES = 5;
/** Upper bound on MultiPV requests; no legal position needs more in practice. */
export const RANK_MAX_LINES = 64;

export type MedalName = "gold" | "silver" | "bronze";
export type WithheldReason = "hint" | "takeback" | null;
export type MedalStatus = "pending" | "verified" | "unavailable";
export type RankTier = "elite" | "strong" | "middling" | "weak";

export type RankedLine = {
  uci: string;
  scoreCp: number | null;
  mate: number | null;
  /** Later moves of this line, including the first, when the search sent them. */
  pv?: string[];
};

/** How many of Stockfish's own choices to keep and show. */
export const ENGINE_CHOICE_COUNT = 3;
/**
 * A second or third choice within this many centipawns of the best is close
 * enough to teach as a repertoire move. It matches the "equivalent" engine
 * band: about 0.4 of a pawn.
 */
export const REPERTOIRE_GAP_CP = 40;

export type EngineChoice = {
  san: string;
  uci: string;
  rank: number;
  /** Centipawns behind the best line of the same search. 0 for the best. */
  behindCp: number | null;
  mate: number | null;
};

export type ScoredDecision = {
  medal: MedalName | null;
  earnedMedal: MedalName | null;
  rank: number | null;
  medalStatus: MedalStatus;
  withheld: WithheldReason;
  moveScore: number | null;
  outsideTop: boolean;
  /** Number of legal moves the rank is measured against. */
  total: number | null;
  /** How many of those moves the engine actually ranked. */
  rankedCount: number | null;
  /** rank / total as a percentage; lower is better ("top 25%"). */
  percentile: number | null;
};

export type MedalFacts = {
  san?: string;
  medal?: MedalName | null;
  earnedMedal?: MedalName | null;
  rank?: number | null;
  medalStatus?: MedalStatus;
  withheld?: WithheldReason;
  moveScore?: number | null;
  outsideTop?: boolean;
  total?: number | null;
  rankedCount?: number | null;
  percentile?: number | null;
};

function compareLines(a: RankedLine, b: RankedLine): number {
  const aMate = a.mate != null;
  const bMate = b.mate != null;
  if (aMate && bMate) {
    if (a.mate! > 0 && b.mate! > 0) return a.mate! - b.mate!;
    if (a.mate! > 0 && b.mate! <= 0) return -1;
    if (a.mate! <= 0 && b.mate! > 0) return 1;
    return a.mate! - b.mate!;
  }
  if (aMate && !bMate) return a.mate! > 0 ? -1 : 1;
  if (!aMate && bMate) return b.mate! > 0 ? 1 : -1;
  return (b.scoreCp ?? Number.NEGATIVE_INFINITY) - (a.scoreCp ?? Number.NEGATIVE_INFINITY);
}

function scoreKey(line: RankedLine): string {
  if (line.mate != null) return `m:${line.mate}`;
  if (line.scoreCp != null) return `cp:${line.scoreCp}`;
  return `u:${line.uci}`;
}

function pvMoves(raw: unknown): string[] | undefined {
  const parts = Array.isArray(raw) ? raw : typeof raw === "string" ? raw.split(/\s+/) : [];
  const moves = parts.filter(
    (move): move is string => typeof move === "string" && move.length >= 4
  );
  return moves.length ? moves : undefined;
}

export function linesFromCandidates(raw: unknown): RankedLine[] {
  if (!Array.isArray(raw)) return [];
  const best = new Map<string, RankedLine>();
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as { uci?: unknown; scoreCp?: unknown; mate?: unknown; pv?: unknown };
    if (typeof row.uci !== "string" || row.uci.length < 4) continue;
    const pv = pvMoves(row.pv);
    const line: RankedLine = {
      uci: row.uci,
      scoreCp: typeof row.scoreCp === "number" ? row.scoreCp : null,
      mate: typeof row.mate === "number" ? row.mate : null,
      ...(pv ? { pv } : {}),
    };
    const prev = best.get(line.uci);
    if (!prev || compareLines(line, prev) < 0) best.set(line.uci, line);
    else if (compareLines(line, prev) === 0 && (line.pv?.length || 0) > (prev.pv?.length || 0))
      best.set(line.uci, { ...prev, pv: line.pv });
  }
  return [...best.values()];
}

/**
 * Keep only the lines whose first move is legal in the position. An engine
 * answer that reached the client late can describe another position; such
 * lines would rank the student's move against moves that do not exist here.
 */
export function legalLines(fen: string, lines: RankedLine[]): RankedLine[] {
  let legal: Set<string>;
  try {
    legal = new Set(
      new Chess(fen).moves({ verbose: true }).map((m) => `${m.from}${m.to}${m.promotion || ""}`)
    );
  } catch {
    return lines;
  }
  return lines.filter((line) => legal.has(line.uci));
}

export function rankLines(lines: RankedLine[]): Array<RankedLine & { rank: number }> {
  const sorted = [...lines].sort(compareLines);
  const ranked: Array<RankedLine & { rank: number }> = [];
  let lastKey = "";
  let rank = 0;
  sorted.forEach((line, index) => {
    const key = scoreKey(line);
    if (key !== lastKey) {
      rank = index + 1;
      lastKey = key;
    }
    ranked.push({ ...line, rank });
  });
  return ranked;
}

export function medalForRank(rank: number | null): MedalName | null {
  if (rank === 1) return "gold";
  if (rank === 2) return "silver";
  if (rank === 3) return "bronze";
  return null;
}

function comparable(line: RankedLine): number | null {
  if (line.mate != null) return line.mate > 0 ? 100_000 - line.mate : -100_000 - line.mate;
  return line.scoreCp;
}

/**
 * Points the played move gives up against the best line, both scored in the
 * same search; null when the move is not among the lines.
 */
export function lossFromLines(lines: RankedLine[], uci: string): number | null {
  const best = rankLines(lines)[0];
  const played = lines.find((line) => line.uci === uci);
  if (!best || !played) return null;
  const bestValue = comparable(best);
  const playedValue = comparable(played);
  if (bestValue == null || playedValue == null) return null;
  return Math.max(0, bestValue - playedValue);
}

function bareSan(san: string): string {
  return san.replace(/[+#?!]/g, "");
}

function joinAnd(parts: string[]): string {
  if (parts.length <= 1) return parts[0] || "";
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

/** "about 15 centipawns behind", "also best", or a mate phrase. Never a raw mate-scale number. */
function gapPhrase(choice: EngineChoice, best: EngineChoice): string {
  if (best.mate != null && best.mate > 0) {
    if (choice.mate != null && choice.mate > 0) {
      return choice.mate === best.mate ? "the same mate" : `mate in ${choice.mate}`;
    }
    return "not the mate";
  }
  if (choice.mate != null && choice.mate < 0) return "and loses";
  const n = choice.behindCp == null ? null : Math.round(choice.behindCp);
  if (n == null) return "unscored";
  if (n <= 0) return "also best";
  if (n > 10000) return "a long way behind";
  const unit = n === 1 ? "centipawn" : "centipawns";
  return `about ${n} ${unit} behind`;
}

function gapTowardBest(choice: EngineChoice, best: EngineChoice): string {
  const phrase = gapPhrase(choice, best);
  return phrase.endsWith("behind") ? `${phrase} ${best.san}` : phrase;
}

function placeName(rank: number): string {
  if (rank === 1) return "first";
  if (rank === 2) return "second";
  if (rank === 3) return "third";
  return ordinal(rank);
}

function countWord(count: number): string {
  if (count === 2) return "two";
  if (count === 3) return "three";
  return String(count);
}

/**
 * Stockfish's first three choices in this position, best first, with the
 * centipawn gap from the same search that ranked the played move.
 */
export function topEngineChoices(
  fen: string,
  lines: RankedLine[],
  limit = ENGINE_CHOICE_COUNT
): EngineChoice[] {
  let ranked: Array<RankedLine & { rank: number }>;
  try {
    ranked = rankLines(legalLines(fen, lines));
  } catch {
    return [];
  }
  const best = ranked[0];
  if (!best) return [];
  const out: EngineChoice[] = [];
  for (const line of ranked.slice(0, limit)) {
    if (line.uci.length < 4) continue;
    try {
      const moved = new Chess(fen).move({
        from: line.uci.slice(0, 2),
        to: line.uci.slice(2, 4),
        ...(line.uci.length > 4 ? { promotion: line.uci[4] } : {}),
      });
      if (!moved) continue;
      out.push({
        san: moved.san,
        uci: line.uci,
        rank: line.rank,
        behindCp: lossFromLines(ranked, line.uci),
        mate: line.mate,
      });
    } catch {
      /* A line that is not legal here is not one of this position's choices. */
    }
  }
  return out;
}

/**
 * Names Stockfish's second and third choices and the gap from its first.
 * A move inside the first three and within REPERTOIRE_GAP_CP is described as
 * a repertoire choice. A move outside them is named against that list.
 */
export function describeEngineChoices(
  playedSan: string,
  choices: EngineChoice[] | null | undefined,
  playedBehindCp?: number | null
): string {
  if (!choices || choices.length < 2) return "";
  const best = choices[0];
  const mine = choices.find((choice) => bareSan(choice.san) === bareSan(playedSan));
  const others = choices.filter((choice) => bareSan(choice.san) !== bareSan(playedSan));

  if (mine && mine.rank === best.rank) {
    if (others.length === 1) {
      return `Stockfish's next choice is ${others[0].san}, ${gapPhrase(others[0], best)}.`;
    }
    const [first, second] = others;
    const secondGap = gapPhrase(second, best).replace(/ centipawns? behind$/, " behind");
    return `Stockfish's next two are ${first.san}, ${gapPhrase(first, best)}, and ${second.san}, ${secondGap}.`;
  }

  if (mine) {
    const companion = choices.find((choice) => choice.uci !== mine.uci && choice.uci !== best.uci);
    const close =
      mine.behindCp != null &&
      mine.behindCp > 0 &&
      mine.behindCp <= REPERTOIRE_GAP_CP &&
      !(best.mate != null && best.mate > 0);
    let text = `${playedSan} is Stockfish's ${placeName(mine.rank)} choice, ${gapTowardBest(mine, best)}.`;
    if (companion) {
      text += ` The ${placeName(companion.rank)} is ${companion.san}, ${gapTowardBest(companion, best)}.`;
    }
    if (close) text += " That gap is small enough to treat as a repertoire choice.";
    return text;
  }

  const labels = choices.map((choice, index) =>
    index === 0 ? choice.san : `${choice.san} (${gapPhrase(choice, best)})`
  );
  const listed = `Stockfish's first ${countWord(choices.length)} are ${joinAnd(labels)}.`;
  const bestMates = best.mate != null && best.mate > 0;
  const behind = playedBehindCp == null ? null : Math.round(playedBehindCp);
  if (!bestMates && behind != null && behind > 0 && behind <= 10000) {
    const unit = behind === 1 ? "centipawn" : "centipawns";
    return `${listed} ${playedSan} is about ${behind} ${unit} behind ${best.san}.`;
  }
  if (!bestMates && behind != null && behind > 10000) {
    return `${listed} ${playedSan} is a long way behind ${best.san}.`;
  }
  return `${listed} ${playedSan} is outside the first ${countWord(choices.length)}.`;
}

export function provisionalMoveScore(best: RankedLine, played: RankedLine): number | null {
  const bestValue = comparable(best);
  const playedValue = comparable(played);
  if (bestValue == null || playedValue == null) return null;
  const loss = Math.max(0, bestValue - playedValue);
  return Math.round(100 * Math.exp(-loss / 150));
}

export function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  const mod10 = n % 10;
  if (mod10 === 1) return `${n}st`;
  if (mod10 === 2) return `${n}nd`;
  if (mod10 === 3) return `${n}rd`;
  return `${n}th`;
}

export function percentileOf(rank: number | null, total: number | null): number | null {
  if (rank == null || total == null || total <= 0) return null;
  return Math.round((rank / total) * 100);
}

export function rankTier(percentile: number | null): RankTier | null {
  if (percentile == null) return null;
  if (percentile <= 12) return "elite";
  if (percentile <= 35) return "strong";
  if (percentile <= 65) return "middling";
  return "weak";
}

/**
 * Decide the medal and the rank of the played move among every legal move.
 * `legalCount` is the number of legal moves in the position; when the engine
 * returned fewer lines than that (an older server caps MultiPV), a move that
 * is missing from the lines is reported as beyond the ranked count rather
 * than guessed. Lines that cover every legal move yet miss the played move
 * cannot be this position's, so the rank is left unavailable.
 */
export function decideMedal(args: {
  uci: string;
  lines: RankedLine[];
  assisted: boolean;
  withheld: WithheldReason;
  legalCount?: number | null;
}): ScoredDecision {
  const empty: ScoredDecision = {
    medal: null,
    earnedMedal: null,
    rank: null,
    medalStatus: "unavailable",
    withheld: args.withheld,
    moveScore: null,
    outsideTop: false,
    total: null,
    rankedCount: null,
    percentile: null,
  };
  const ranked = rankLines(args.lines);
  if (args.legalCount === 1) {
    // A forced move is trivially the best available; it earns no medal.
    return {
      ...empty,
      rank: 1,
      medalStatus: "verified",
      moveScore: 100,
      total: 1,
      rankedCount: Math.max(1, ranked.length),
      percentile: 0,
    };
  }
  if (ranked.length < 2) return empty;
  const total = Math.max(ranked.length, args.legalCount ?? 0);
  const hit = ranked.find((line) => line.uci === args.uci);
  if (!hit && args.legalCount != null && ranked.length >= args.legalCount) return empty;
  if (!hit) {
    return {
      ...empty,
      medalStatus: "verified",
      outsideTop: true,
      total,
      rankedCount: ranked.length,
    };
  }
  const earned = medalForRank(hit.rank);
  const moveScore = provisionalMoveScore(ranked[0], hit);
  const percentile = percentileOf(hit.rank, total);
  if (!earned) {
    return {
      ...empty,
      rank: hit.rank,
      medalStatus: "verified",
      moveScore,
      outsideTop: hit.rank > 3,
      total,
      rankedCount: ranked.length,
      percentile,
    };
  }
  return {
    medal: args.assisted ? null : earned,
    earnedMedal: earned,
    rank: hit.rank,
    medalStatus: "verified",
    withheld: args.assisted ? args.withheld : null,
    moveScore,
    outsideTop: false,
    total,
    rankedCount: ranked.length,
    percentile,
  };
}

export function medalStreak(entries: MedalFacts[]): number {
  let streak = 0;
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const entry = entries[i];
    if (entry.medalStatus === "pending") break;
    if (entry.medal === "gold" || entry.medal === "silver" || entry.medal === "bronze") {
      streak += 1;
      continue;
    }
    break;
  }
  return streak;
}

/** "6th / 24", "1st / 20", or "beyond 5th / 24" when the ranking was partial. */
export function rankLabel(entry: MedalFacts | null | undefined): string {
  if (!entry || entry.medalStatus !== "verified") return "";
  if (entry.rank != null) {
    return entry.total ? `${ordinal(entry.rank)} / ${entry.total}` : ordinal(entry.rank);
  }
  if (entry.outsideTop && entry.rankedCount) {
    return `beyond ${ordinal(entry.rankedCount)}${entry.total ? ` / ${entry.total}` : ""}`;
  }
  return "";
}

export function medalLiveText(entry: MedalFacts | null | undefined): string {
  if (!entry?.san) return "";
  if (entry.medalStatus === "pending") return `Ranking ${entry.san} against every legal move.`;
  if (entry.medalStatus !== "verified") {
    return `Engine rank unavailable for ${entry.san}. No medal was guessed.`;
  }
  if (entry.total === 1 && entry.rank === 1) return `${entry.san} was the only legal move.`;
  const place =
    entry.rank != null
      ? entry.total
        ? `the ${ordinal(entry.rank)} best of ${entry.total} legal moves${
            entry.percentile != null ? ` (top ${entry.percentile}%)` : ""
          }`
        : `ranked ${ordinal(entry.rank)}`
      : entry.outsideTop && entry.rankedCount
        ? `beyond the ${entry.rankedCount} moves the engine ranked${entry.total ? ` of ${entry.total}` : ""}`
        : "unranked";
  if (entry.medal) {
    const label = entry.medal[0].toUpperCase() + entry.medal.slice(1);
    return `${label} medal. ${entry.san} is ${place}.`;
  }
  if (entry.withheld === "hint") {
    return `${entry.san} is ${place}. No medal, because a hint was used.`;
  }
  if (entry.withheld === "takeback") {
    return `${entry.san} is ${place}. No medal, because the move was taken back and tried again.`;
  }
  if (entry.rank != null && entry.rank <= 3) return `${entry.san} is ${place}.`;
  return `${entry.san} is ${place}. No medal.`;
}

export type RankSummary = {
  ranked: number;
  averagePercentile: number | null;
  gold: number;
  silver: number;
  bronze: number;
  best: { index: number; entry: MedalFacts } | null;
  worst: { index: number; entry: MedalFacts } | null;
};

/** Session-wide view of the rankings: average percentile, medals, best and worst moves. */
export function summarizeRanks(entries: MedalFacts[]): RankSummary {
  const ranked = entries
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => entry.medalStatus === "verified" && entry.percentile != null);
  const count = (name: MedalName) => entries.filter((e) => e.medal === name).length;
  if (!ranked.length) {
    return {
      ranked: 0,
      averagePercentile: null,
      gold: count("gold"),
      silver: count("silver"),
      bronze: count("bronze"),
      best: null,
      worst: null,
    };
  }
  const total = ranked.reduce((sum, { entry }) => sum + (entry.percentile as number), 0);
  const sorted = [...ranked].sort(
    (a, b) => (a.entry.percentile as number) - (b.entry.percentile as number)
  );
  return {
    ranked: ranked.length,
    averagePercentile: Math.round(total / ranked.length),
    gold: count("gold"),
    silver: count("silver"),
    bronze: count("bronze"),
    best: sorted[0],
    worst: ranked.length > 1 ? sorted[sorted.length - 1] : null,
  };
}
